import type { AxiosRequestConfig } from 'axios'
import type { ResultOf, VariablesOf } from 'gql.tada'
import type { ASTNode } from 'graphql'

import type { introspection } from '~/graphql-env.d.ts'
import { initGraphQLTada } from 'gql.tada'
import { print } from 'graphql'
import KnowError from '~/exceptions/KnownError.ts'
import http from './financial-bff.http'

export const graphql = initGraphQLTada<{
  introspection: introspection
}>()

export type { FragmentOf, ResultOf, VariablesOf } from 'gql.tada'
export { readFragment } from 'gql.tada'

/**
 * Types
 */

interface GraphQLRes<T = unknown> {
  data: T
  errors: Error[] | null
}

/**
 * GraphQL Tada GQL instance
 */

export const gql = initGraphQLTada<{ introspection: introspection }>()

/**
 * Execute a GraphQL query with variables and return the data
 *
 * @param document - GraphQL document
 * @param variables - Variables for the document
 * @param config - Axios request config
 * @returns GraphQL response data
 */
export async function execute<T extends ASTNode>(document: T, variables: VariablesOf<typeof document>, config?: Partial<AxiosRequestConfig>) {
  const query = print(document)
  const payload = { query, variables }
  const res = await http.post<GraphQLRes<ResultOf<T>>>('', payload, config)

  if (res.data.errors) {
    throw new KnowError(res.data.errors[0].message)
  }

  return res.data.data
}

/**
 * Execute a GraphQL mutation with variables and files and return the data
 *
 * @param document - GraphQL document
 * @param variables - Variables for the document
 * @param files - Files to upload
 * @param config - Axios request config
 * @returns GraphQL response data
 */
export async function executeFileUpload<T extends ASTNode>(
  document: T,
  variables: VariablesOf<typeof document>,
  files: { [key: string]: File },
  config?: Partial<AxiosRequestConfig>,
) {
  const query = print(document)
  const formData = new FormData()

  // Monta operations
  formData.append('operations', JSON.stringify({
    query,
    variables: Object.keys(files).reduce((acc, key) => {
      acc[key] = null
      return acc
    }, variables),
  }))

  // Monta map
  const map: Record<string, string[]> = {}
  let fileIndex = 0
  for (const key of Object.keys(files)) {
    map[fileIndex] = [`variables.${key}`]
    fileIndex++
  }
  formData.append('map', JSON.stringify(map))

  // Add arquivos
  fileIndex = 0
  for (const key of Object.keys(files)) {
    formData.append(`${fileIndex}`, files[key])
    fileIndex++
  }

  const res = await http.post<GraphQLRes<ResultOf<T>>>('', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    ...config,
  })

  if (res.data.errors) {
    throw new KnowError(res.data.errors[0].message)
  }

  return res.data.data
}
